import { getState, setState, subscribe, type Player, type ChatMessage } from '../store';
import { wsUrl, leaveGame, getChatMessages, getLobbyChatHistory } from '../api/game-api';
import type { ChatMessageData } from '../api/game-api';
import { ReconnectingWebSocket } from '../api/websocket';
import { navigate } from '../router';
import { showReconnecting, hideStatus } from '../components/connection-status';
import { normalizeTeams } from '../utils/lobby-helpers';
import { escapeHtml } from '../utils/escape';
import { copyToClipboard } from '../utils/clipboard';
import { showCopyToast, dismissAllTeamToasts } from '../components/team-event-toast';
import { createEmojiPicker } from '../components/emoji-picker';
import { teamColor } from '../utils/team-color';
import { acknowledgeChatThrough } from '../utils/chat-poll';
import { anchorFromServer } from '../utils/time-utils';
import { fetchPlayerState } from '../utils/auth-failure';
import { ReconcileGate } from '../utils/reconcile-gate';
import { createViewLifecycle, type ViewLifecycle } from '../utils/view-lifecycle';
import { installVisibilityReconcile } from '../utils/visibility-reconciler';

let ws: ReconnectingWebSocket | null = null;
let kebabTeardown: (() => void) | null = null;
// Reconcile gate — owns the concurrent-collapse lock + AbortController + 15s
// timeout that previously lived inline in `reconcileLobbyAgainstServer`. iOS
// Safari fires visibilitychange + pageshow nearly simultaneously on resume;
// without the lock, two parallel /player-state fetches would both resolve
// `gameStatus=ACTIVE` → two parallel `destroyLobbyView()` + `navigate('riddle')`,
// double-firing GAME_STARTED promotion. iPhone Safari cold-start TLS pool can
// hang the fetch indefinitely; without the timeout, the lock would hold forever
// and the 5s G3 poll would bail at `gate.isInflight`. See
// `pwa/src/utils/reconcile-gate.ts` for the timeout-history rationale.
const reconcileGate = new ReconcileGate();
// Plan B Task 6 (2026-05-11) — visibilityHandler, pageshowHandler, and
// lobbyReconcilePoll are now owned by activeLc. The poll interval is
// registered via activeLc.setInterval; the visibility/pageshow listeners are
// installed via the visibility-reconciler foundation (see Step 3 of
// renderLobbyView). All three resources tear down through activeLc.cleanup()
// in destroy. kebabTeardown / storeUnsubscribe / historyAbort /
// lobbyHistoryAbort cleanups also moved into lc via adopt() / onDestroy().
let activeLc: ViewLifecycle | null = null;
// v0.79.18 — defense-in-depth poll cadence. Symmetric to location-view's
// orgAdvancePoll which uses the same 5s interval. If you change either,
// change both for consistency.
const LOBBY_RECONCILE_POLL_INTERVAL_MS = 5000;

// /investigate 2026-05-02 — Bug 1 (Edge refresh wiped lobby chat).
//
// Cold-mount fetch of LOBBY-channel history. WS frames already render
// fine via the LOBBY_CHAT handler, but the page used to come back empty
// after F5 because no fetch path existed for the player. We hydrate
// from `getLobbyChatHistory`, then suppress any WS frame whose timestamp
// is at-or-before the highest history timestamp — that's the rows we
// already painted. Server inserts under `System.currentTimeMillis()`
// (ms granularity, single-threaded INSERT), so timestamp is a stable
// id-substitute for the dedup window.
let lobbyHistoryCutoff = 0;
let lobbyHistoryAbort: AbortController | null = null;

// Session 87 — lobby targeted-team feature.
//
// Player in pre-start lobby subscribes to the existing team-chat WebSocket
// alongside the lobby WS. This surfaces org→team messages live (backend
// already accepts org→team writes pre-start; the only missing piece was
// PWA player-side subscription). Variant B path from /autoplan 2026-04-26.
//
// Org messages are inline-merged into the existing #lobby-messages log
// (one log, one scroll, one aria-live) using session-52 amber org-bubble
// styling — NOT a separate section. NO emoji marker (DESIGN.md anti-pattern).
//
// State machine:
//   - On mount: subscribe to chat WS for current state.teamName + history hydrate
//   - On state.teamName change pre-start: close+reopen chat WS for new team
//   - On GAME_STARTED: explicit destroyLobbyView() before navigate('riddle')
//     so location-view's chat-view doesn't double-subscribe to the same WS
//   - On unmount: close both WS, abort history, unsubscribe from store
let chatWs: ReconnectingWebSocket | null = null;
let storeUnsubscribe: (() => void) | null = null;
let historyAbort: AbortController | null = null;
let lastTeamName: string | null = null;
const seenChatIds = new Set<string>();

// Session 68 — lobby polish.
//
// Layout moved from vertical card-pile (hero code chip → spinner row → rules
// card → teams block → chat 200px → big leave button) to a flex-column
// chat-dominant shell:
//   - Header row: h1 with pulse-dot + kebab button (aria-haspopup="menu")
//   - Guidance paragraph (the "organizer will start" line moved up here)
//   - Rules <details> collapsed by default with a 60-char preview in <summary>
//   - Teams <details open> with a dynamic count "(N)" in <summary>
//   - Chat taking flex:1 of remaining viewport
// Code + leave-lobby moved into the kebab (with tap-to-copy + toast).
//
// Two CRITICAL bugs caught by /autoplan eng review BEFORE implementation:
//   1. renderPlayers() must own both the count span AND the empty-state row
//      — #lobby-empty div was dropped, so reading `emptyEl.style.display`
//      would throw on null.
//   2. destroyLobbyView() must tear down the kebab (body-attached DOM +
//      document click-outside listener), otherwise navigating away with the
//      menu open leaks listeners across views.
//
// The team-event-toast root survives unmount (DESIGN.md one-toast-surface
// rule) — the showCopyToast() function handles its own lifecycle.

/**
 * Normalize rules for the <summary> preview.
 *
 * Steps:
 *   1. Strip Unicode bidi override chars (U+202A..U+202E, U+2066..U+2069) —
 *      otherwise a malicious / accidental RLO in the rules body can flip
 *      the visual direction of the preview and obscure the text.
 *   2. Collapse all whitespace runs into single spaces, then trim.
 *   3. Slice to 60 CODE POINTS (not UTF-16 code units) using `Array.from`
 *      so surrogate pairs from emoji don't split mid-character and render
 *      as 0xFFFD replacement glyphs.
 *   4. Append ellipsis only if truncation actually happened.
 *
 * Session 68 /review finding #3.
 */
const BIDI_CONTROLS_RE = /[\u202A-\u202E\u2066-\u2069]/g;

export function buildRulesPreview(rules: string | null | undefined): string {
  if (!rules) return '';
  const stripped = rules.replace(BIDI_CONTROLS_RE, '');
  const normalized = stripped.replace(/\s+/g, ' ').trim();
  if (normalized.length === 0) return '';
  const codePoints = Array.from(normalized);
  if (codePoints.length <= 60) return normalized;
  return codePoints.slice(0, 60).join('') + '…';
}

/**
 * /investigate 2026-04-27 — Lobby reconcile against server snapshot.
 *
 * iOS Safari aggressively suspends backgrounded tabs. The WebSocket gets
 * killed during suspension, so a GAME_STARTED broadcast that fires while
 * the tab is asleep arrives at no one. On resume the tab restores from
 * its in-memory state (still `status: 'LOBBY'`) — the only signal that
 * the game has advanced is gone.
 *
 * Same architectural class as session-89 captain-desync (location-view
 * `reconcileFromServer`). WS = delta, DB = state. Cold-mount AND
 * visibility-resume must both reconcile against the server snapshot.
 *
 * Returns `true` if the lobby was promoted out (game ACTIVE/FINISHED),
 * `false` if the lobby is still authoritative. On error returns `false`
 * — let the user keep the lobby they have, the WS will catch up if it
 * eventually reconnects.
 */
export async function reconcileLobbyAgainstServer(): Promise<boolean> {
  return reconcileGate.run(false, false, async (signal) => {
    const state = getState();
    if (state.fullOffline) return false;
    if (!state.gameCode || !state.playerId || !state.playerToken) return false;

    const result = await fetchPlayerState({
      gameCode: state.gameCode,
      playerId: state.playerId,
      playerToken: state.playerToken,
      context: 'reconcile-lobby',
      signal,
    });
    if (result.kind === 'auth-failed') {
      // Wrapper already dispatched. Returning true signals «lobby promoted
      // out» — main.ts handler tears down view + navigate('join') + clear
      // data. Symmetric with FINISHED/ACTIVE branches below.
      return true;
    }
    if (result.kind === 'network-blip') return false;
    const ps = result.data;
    // /review 2026-04-27 v0.79.16 (F1) — route guard. The fetch is async; the
    // user may have pressed «leave lobby» mid-flight. By the time the response
    // resolves, the router has swapped to /join (or /riddle), and a stale
    // resolver here would force-navigate back, ripping the user away from
    // where they chose to go.
    if (typeof window !== 'undefined' && window.location.hash !== '#/lobby') {
      return false;
    }
    if (ps.gameStatus === 'FINISHED' || ps.finishedAt) {
      setState({ status: 'FINISHED' });
      destroyLobbyView();
      // Same-hash guard symmetric with poll-player-state.ts (session 76 — prod
      // game 757873 boot-animation replay loop).
      if (typeof window === 'undefined' || window.location.hash !== '#/results') {
        navigate('results');
      }
      return true;
    }
    if (ps.gameStatus === 'ACTIVE') {
      // Anchor seeded from `locationEnteredAt + serverNow` via `anchorFromServer`
      // (clock-skew correction). Pre-fix used `Date.now()` which would RESET the
      // timer to 0 on the resumed tab — but the team has been at this location
      // since startGame fired N seconds ago. The timer must reflect that elapsed
      // time, not the moment the player happened to reopen Safari. See MEMORY.md
      // timer-anchor contract + `pwa/tests/team-sync-timer-anchor-contract.test.ts`.
      const anchor = anchorFromServer(ps.locationEnteredAt, ps.serverNow);
      setState({ status: 'ACTIVE', locationStartedAt: anchor, gameStartedAt: anchor });
      destroyLobbyView();
      navigate('riddle');
      return true;
    }
    return false;
  });
}

export function renderLobbyView(container: HTMLElement): void {
  const state = getState();
  // Cold-mount reconcile — fire-and-forget. If the server says the game
  // is already ACTIVE, this navigates away before the user notices the
  // lobby render. Errors are non-fatal (the WS will catch up if it ever
  // reconnects). Same fire-and-forget pattern as location-view's mount.
  void reconcileLobbyAgainstServer();

  // Plan B Task 6 (2026-05-11) — visibility/pageshow + 5s poll lifecycle is
  // delegated to `activeLc`. The composite callback to
  // `installVisibilityReconcile` does BOTH the reconcile AND the Bug 2
  // forceReconnect on BOTH the lobby ws AND chat ws (pre-extraction handler
  // called all three; post-extraction composite-callback preserves all three),
  // so the pre-extraction inline-handler shape is preserved structurally —
  // both resume paths (visibilitychange + pageshow) run the same recovery
  // body. Reconcile poll is the v0.79.18 G3 defense-in-depth pacing for
  // iOS Safari edge cases where neither pageshow nor visibilitychange fires.
  activeLc = createViewLifecycle();

  // Plan B Task 10 (2026-05-11) — team-event-toast singleton teardown for the
  // lobby's `showCopyToast` callers (team-name copy at ~line 505, kebab-copy
  // code at ~line 900). `showCopyToast` itself is LC-free by design (called
  // from non-view contexts too — see team-event-toast.ts comments), so no
  // bodyOverlays registration happens on the show side. Register the sweep
  // unconditionally on render so kebab → leave-game right after a copyToast
  // doesn't leak the 1.2s toast onto /join — same class as the v0.80.9
  // connection-bar bug. `dismissAllTeamToasts` is idempotent (walks the
  // module registry; no-op when empty), so adopting it on every mount is
  // cheap and pin-safe.
  activeLc.onDestroy(dismissAllTeamToasts);

  activeLc.setInterval(() => {
    void reconcileLobbyAgainstServer();
  }, LOBBY_RECONCILE_POLL_INTERVAL_MS);

  // iOS Safari WS-suspend resume — reconcile + Bug 2 forceReconnect on BOTH
  // sockets. /investigate 2026-05-02 — without `ws?.forceReconnect()` AND
  // `chatWs?.forceReconnect()`, iOS Safari leaves either socket in
  // stuck-CONNECTING after bg→fg, banner «Переподключение…» persists forever.
  // The reconcile is HTTP-only; live frames silently never resume without the
  // explicit kick.
  installVisibilityReconcile(activeLc, () => {
    void reconcileLobbyAgainstServer();
    ws?.forceReconnect();
    chatWs?.forceReconnect();
  }, '#/lobby');
  const hasRules = !!(state.gameRules && state.gameRules.trim().length > 0);
  const rulesPreview = hasRules ? buildRulesPreview(state.gameRules) : '';
  // Session 87 — split visibility from send permission, but PRESERVE session
  // 75's CAPTAINS_ONLY-non-captain → "hide everything" behavior (the user
  // explicitly asked: "non-captain has no way to engage with what they see").
  //
  // Resolution matrix:
  //   - OFF:                       block visible (org→team renders), composer hidden
  //   - CAPTAINS_ONLY + captain:   block visible, composer visible
  //   - CAPTAINS_ONLY + grunt:     block ENTIRELY HIDDEN (session 75 contract)
  //   - ALL_PLAYERS:               block visible, composer visible
  //
  // This means non-captains in CAPTAINS_ONLY mode don't see org→team
  // messages live in lobby — they receive them retroactively when
  // location-view's chat-view loads history post-start. The captain who
  // sees them live can relay if needed (the org's expected workflow when
  // they enabled CAPTAINS_ONLY mode in the first place).
  const captainsOnlyLocked = state.lobbyChatMode === 'CAPTAINS_ONLY' && !state.isCaptain;
  const blockHidden = captainsOnlyLocked;
  const composerHidden = blockHidden || state.lobbyChatMode === 'OFF';
  const canSend = !composerHidden;
  // Eyebrow adapts: "ЧАТ" when peer chat works, "СООБЩЕНИЯ" when the log
  // is read-only (OFF mode — only org→team messages will ever appear).
  const eyebrowText = state.lobbyChatMode === 'OFF' ? 'СООБЩЕНИЯ' : 'ЧАТ';

  // Rules <details> block. NOTE — no backticks inside any HTML-comment
  // within this template literal; JS parses them as terminators and the
  // whole renderLobbyView explodes at module-eval time. Tripped twice.
  // 2026-04-26 user request — disclosure affordance for the rules block.
  // Default <details> marker is hidden by .rules-details > summary::marker
  // CSS below (intentional, since the native triangle clashes with Mission
  // Control aesthetic). Without it the user has zero signal the block is
  // expandable. Add an inline SVG chevron right-aligned in the summary
  // header row; rotates 180° when [open] via the .rules-chevron CSS rule.
  // Text-only marker (DESIGN.md anti-pattern: emoji as design element),
  // currentColor inherits text-secondary so it doesn't compete with the
  // sacred amber accent.
  const rulesHtml = hasRules ? `<details class="card rules-details" style="padding: var(--sp-md); margin-bottom: var(--sp-md);">
        <summary style="cursor: pointer; display: flex; flex-direction: column; gap: var(--sp-xs);">
          <span style="display: flex; align-items: center; justify-content: space-between; gap: var(--sp-sm);">
            <span class="text-eyebrow">ПРАВИЛА</span>
            <svg class="rules-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="color: var(--text-secondary); flex-shrink: 0; transition: transform 150ms ease-out;">
              <polyline points="3 6 8 11 13 6"></polyline>
            </svg>
          </span>
          <span class="rules-preview text-body" dir="auto" style="color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHtml(rulesPreview)}</span>
        </summary>
        <div class="text-body" dir="auto" style="white-space: pre-wrap; max-height: 240px; overflow-y: auto; overscroll-behavior: contain; margin-top: var(--sp-sm);">${escapeHtml(state.gameRules!)}</div>
      </details>` : '';

  // 2026-04-26 user request — emoji picker in lobby chat, mirror of
  // chat-view.ts:307-316. The #lobby-emoji-panel anchor sits ABOVE the
  // input row so the picker pops up over the message log instead of
  // being clipped by the chat container's bottom edge. Same picker
  // component (`createEmojiPicker`) the in-game chat uses, same single-
  // tap-to-toggle UX.
  // Session 87 — log container rendered when block visible (F9 for OFF mode,
  // session 75 still hides everything for CAPTAINS_ONLY-non-captain).
  // Composer (emoji + input + send) only when canSend.
  const chatHtml = blockHidden ? '' : `<div id="lobby-chat" style="flex: 1 1 auto; display: flex; flex-direction: column; min-height: 200px; margin-top: var(--sp-md);">
        <div class="text-eyebrow" style="margin-bottom: var(--sp-sm);">${eyebrowText}</div>
        <div id="lobby-messages" role="log" aria-live="polite" aria-atomic="false" style="flex: 1 1 auto; min-height: 0; overflow-y: auto; background: var(--bg-card); border-radius: var(--radius-lg); padding: var(--sp-md); margin-bottom: var(--sp-sm);"></div>
        ${canSend ? `<div id="lobby-emoji-panel" style="position:relative;"></div>
          <div style="display: flex; gap: var(--sp-sm);">
          <button id="lobby-emoji-btn" class="btn btn-outline" style="min-width:44px;min-height:44px;padding:0;font-size:20px;" aria-label="Эмодзи">😊</button>
          <input type="text" id="lobby-chat-input" class="input" placeholder="Сообщение..." style="flex: 1;" maxlength="500">
          <button class="btn btn-primary" id="lobby-send-btn" style="min-width: 56px; padding-left: var(--sp-sm); padding-right: var(--sp-sm);">\u2192</button>
        </div>` : ''}
      </div>`;

  container.innerHTML = `
    <div class="screen screen-enter screen-split">
      <div class="screen-content">
        <header style="display: flex; align-items: center; justify-content: space-between; gap: var(--sp-md); margin-bottom: var(--sp-sm);">
          <h1 class="text-display-medium" style="flex: 1; min-width: 0; margin: 0; display: inline-flex; align-items: center; gap: var(--sp-sm);">
            <span class="lobby-pulse-dot" aria-hidden="true"></span>
            <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">Ожидание старта игры</span>
          </h1>
          <button id="lobby-kebab-btn" class="btn btn-outline" aria-label="Меню лобби" aria-haspopup="menu" aria-expanded="false"
                  style="min-height: 40px; width: 40px; padding: 0; font-size: 20px; flex-shrink: 0;">\u22EE</button>
        </header>

        <p class="text-body" style="color: var(--text-hint); margin: 0 0 var(--sp-lg); font-size: 14px;">
          Организатор запустит игру, когда все будут готовы
        </p>

        ${rulesHtml}
      </div>

      <div class="screen-controls">
        <details class="lobby-teams-details" open style="margin-bottom: var(--sp-md);">
          <summary style="cursor: pointer; margin-bottom: var(--sp-sm); display: flex; align-items: center; gap: var(--sp-sm);">
            <span class="text-eyebrow">КОМАНДЫ</span>
            <span id="lobby-teams-count" style="color: var(--text-secondary); font-weight: 400; font-size: 14px; letter-spacing: 0;">(0)</span>
          </summary>
          <div id="lobby-players"></div>
        </details>

        ${chatHtml}
      </div>
    </div>
    <style>
      .lobby-pulse-dot {
        display: inline-block;
        width: 8px; height: 8px;
        border-radius: 50%;
        background: var(--amber);
        animation: lobbyPulse 1.2s ease-in-out infinite;
        flex-shrink: 0;
      }
      @keyframes lobbyPulse { 0%, 100% { opacity: 0.4 } 50% { opacity: 1 } }
      @media (prefers-reduced-motion: reduce) {
        .lobby-pulse-dot { animation: none; opacity: 0.85; }
      }
      .rules-details[open] .rules-preview { display: none; }
      .rules-details[open] .rules-chevron { transform: rotate(180deg); }
      @media (prefers-reduced-motion: reduce) {
        .rules-chevron { transition: none; }
      }
      .rules-details > summary,
      .lobby-teams-details > summary { list-style: none; }
      .rules-details > summary::-webkit-details-marker,
      .lobby-teams-details > summary::-webkit-details-marker { display: none; }
      .rules-details > summary::marker,
      .lobby-teams-details > summary::marker { content: ''; }
    </style>
  `;

  const playersEl = container.querySelector<HTMLElement>('#lobby-players')!;
  const countEl = container.querySelector<HTMLElement>('#lobby-teams-count')!;
  const knownIds = new Set<string>();

  function renderPlayers(players: Player[]): void {
    countEl.textContent = `(${players.length})`;
    if (players.length === 0) {
      playersEl.innerHTML = `<div class="text-body" style="color: var(--text-hint); padding: var(--sp-sm) 0;">Пока никого...</div>`;
      return;
    }
    // 2026-04-26 — color team names with the same per-team palette the
    // lobby chat uses, so a captain's chat color matches their team's row
    // in the list above. Numbered circle stays amber (positional indicator,
    // not team identity — keeps DESIGN.md one-amber-per-screen intact for
    // the leftmost glance). p.name in lobby player list IS the team name
    // since session 49 (every player auto-creates/joins a team).
    playersEl.innerHTML = players.map((p, i) => {
      const isNew = !knownIds.has(p.id);
      const color = teamColor(p.name);
      return `
      <div class="${isNew ? 'player-enter' : ''}" style="display: flex; align-items: center; gap: var(--sp-md); padding: var(--sp-sm) 0;">
        <div style="width: 32px; height: 32px; border-radius: 50%; background: var(--amber); color: var(--bg-deep);
                    display: flex; align-items: center; justify-content: center;
                    font-family: var(--font-mono); font-weight: 600; font-size: 14px;">
          ${i + 1}
        </div>
        <span class="text-title" style="color: ${color};">${escapeHtml(p.name)}</span>
      </div>`;
    }).join('');
    players.forEach(p => knownIds.add(p.id));
  }

  renderPlayers(state.players);

  // Kebab menu — body-attached to escape container clipping. Registers its
  // own document-click + Escape listeners; lc tears them down via the
  // onDestroy closure below. kebabTeardown stays module-level because
  // wireLobbyKebab's return reference is captured here once; the lc closure
  // reads the latest module-level handle at cleanup time.
  const kebabBtn = container.querySelector<HTMLButtonElement>('#lobby-kebab-btn')!;
  kebabTeardown = wireLobbyKebab(kebabBtn);
  activeLc.onDestroy(() => {
    if (kebabTeardown) { kebabTeardown(); kebabTeardown = null; }
  });

  ws = new ReconnectingWebSocket(
    wsUrl(`/ws/games/${state.gameCode}/lobby?playerId=${state.playerId}&token=${state.playerToken}`),
    (connected) => {
      setState({ wsConnected: connected });
      if (connected) hideStatus();
      else showReconnecting();
    }
  );

  function handleTeamListUpdate(msg: unknown): void {
    const data = msg as { players?: string[] };
    const teams = normalizeTeams(data.players);
    setState({ players: teams });
    renderPlayers(teams);
  }

  ws.on('PLAYER_LIST', handleTeamListUpdate);
  ws.on('PLAYER_JOINED', handleTeamListUpdate);
  ws.on('PLAYER_LEFT', handleTeamListUpdate);

  ws.on('GAME_STARTED', () => {
    // /review 2026-04-24 — seed stage-1 timer with Date.now(). Backend
    // GAME_STARTED is a game-wide broadcast with no server timestamp
    // attached, AND backend startGame re-stamps Teams.locationEnteredAt
    // = now() for every team inside the same transaction (see
    // GameOrgControlOps.kt). So Date.now() ≈ server's re-stamp moment
    // modulo network latency; any cross-device clock skew on stage 1 is
    // bounded by the skew itself (rarely >5 sec). Subsequent stages get
    // skew-corrected anchors via TeamEvent.ADVANCE broadcasts which DO
    // carry locationEnteredAt + serverNow.
    //
    // gameStartedAt was uninitialised (= 0) for players who joined when
    // gameStarted=false — finish-path computed `now - 0` ≈ 55 years on
    // results screen. Stamp it here alongside locationStartedAt. The
    // gameStarted=true join branch sets it too; both branches converge.
    const now = Date.now();
    setState({ status: 'ACTIVE', locationStartedAt: now, gameStartedAt: now });
    // Session 87 F2 — explicit teardown before navigate. Router cleanup
    // already runs destroyLobbyView before location-view mounts (main.ts),
    // but this defensive call pins the contract: if the router refactor
    // ever changes ordering, our chat WS still won't co-exist with
    // location-view's chat-view subscription. /autoplan eng review caught
    // this as F2 — explicit > clever.
    destroyLobbyView();
    navigate('riddle');
  });

  ws.on('GAME_FINISHED', () => {
    setState({ status: 'FINISHED' });
    navigate('results');
  });

  ws.on('LOBBY_CHAT', (event: any) => {
    // /investigate 2026-05-02 — dedup against history. If the cold-mount
    // history fetch already painted this row (timestamp <= cutoff), the
    // WS frame is a duplicate (server broadcasts to every subscriber, so
    // a fast roundtrip can re-deliver a row we just hydrated). Skip
    // silently. Strict `<=` because INSERT timestamp at ms granularity
    // is monotonic per-row.
    const ts = typeof event?.timestamp === 'number' ? event.timestamp : 0;
    if (ts > 0 && ts <= lobbyHistoryCutoff) return;
    appendLobbyEvent(event);
  });

  const sendBtn = container.querySelector<HTMLButtonElement>('#lobby-send-btn');
  const chatInput = container.querySelector<HTMLInputElement>('#lobby-chat-input');
  if (sendBtn && chatInput) {
    sendBtn.addEventListener('click', () => {
      const text = chatInput.value.trim();
      if (!text) return;
      ws!.send({
        type: 'LOBBY_CHAT',
        message: text,
        playerId: state.playerId,
        token: state.playerToken
      });
      chatInput.value = '';
    });
    chatInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') sendBtn.click();
    });

    // 2026-04-26 — tap-to-reveal team name on mobile. Desktop users see the
    // team via `title` tooltip on hover; phones don't show `title` reliably,
    // so a tap on the colored sender name surfaces the team via the existing
    // toast infra. Delegated on #lobby-messages because chat bubbles are
    // appended dynamically over the lifetime of the lobby.
    const messagesContainer = container.querySelector<HTMLElement>('#lobby-messages');
    if (messagesContainer) {
      messagesContainer.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const sender = target.closest('.lobby-sender') as HTMLElement | null;
        const tn = sender?.dataset.teamName;
        if (tn) showCopyToast(`Команда: ${tn}`, 1500);
      });
    }

    // 2026-04-26 — emoji picker (mirror of chat-view.ts:308-316). Single
    // click toggles the panel; selecting an emoji appends it to the input
    // and re-focuses so the player can keep typing.
    const emojiBtn = container.querySelector<HTMLButtonElement>('#lobby-emoji-btn');
    const emojiPanel = container.querySelector<HTMLElement>('#lobby-emoji-panel');
    if (emojiBtn && emojiPanel) {
      const picker = createEmojiPicker(emojiPanel, (emoji: string) => {
        chatInput.value += emoji;
        chatInput.focus();
      });
      emojiBtn.addEventListener('click', (e) => { e.stopPropagation(); picker.toggle(); });
    }
  }

  ws.connect();

  // /investigate 2026-05-02 — Bug 1 (Edge refresh wiped lobby chat).
  // Fire-and-forget hydration of LOBBY-channel rows. Errors are silent
  // (the WS will deliver new messages anyway; old ones stay missing,
  // same UX as a network blip). Captures playerId at mount; the abort
  // controller lives in module scope so destroyLobbyView can drop it
  // without an extra closure.
  if (state.gameCode && state.playerId) {
    lobbyHistoryAbort?.abort();
    lobbyHistoryAbort = new AbortController();
    void loadLobbyChatHistoryOnMount(state.gameCode, state.playerId, lobbyHistoryAbort.signal);
  }

  // Session 87 — team-chat WS subscription for live org→team messages.
  // Initial subscribe if teamName already set on mount and chat block is
  // visible (CAPTAINS_ONLY-non-captain has nowhere to render — F14 from
  // post-implementation Codex review). Store-subscribe covers the late-
  // bound case (player rejoins under different team pre-start; F1).
  if (state.teamName && state.playerToken && shouldSubscribeChatWs()) {
    lastTeamName = state.teamName;
    subscribeToTeamChat();
  }
  storeUnsubscribe = subscribe((newState) => {
    if (
      newState.teamName !== lastTeamName &&
      newState.teamName &&
      newState.playerToken
    ) {
      lastTeamName = newState.teamName;
      // Tear down stale subscription FIRST so the new one doesn't see
      // dead-team broadcast frames merge into seenChatIds.
      seenChatIds.clear();
      historyAbort?.abort();
      chatWs?.close();
      historyAbort = null;
      chatWs = null;
      // F14 — only re-subscribe if the new mode permits visible rendering.
      if (shouldSubscribeChatWs()) {
        subscribeToTeamChat();
      }
    }
  });

  // Plan B Task 6 (2026-05-11) — store unsubscribe + the two AbortControllers
  // (historyAbort / lobbyHistoryAbort) are mutated mid-render: each
  // subscribeToTeamChat call replaces historyAbort; loadLobbyChatHistoryOnMount
  // is invoked from this render with lobbyHistoryAbort. Using lc.onDestroy
  // (rather than lc.adopt) ensures cleanup reads the LATEST reference at
  // teardown time, not a stale snapshot captured at register time.
  activeLc.onDestroy(() => {
    if (storeUnsubscribe) { storeUnsubscribe(); storeUnsubscribe = null; }
    if (historyAbort) { historyAbort.abort(); historyAbort = null; }
    if (lobbyHistoryAbort) { lobbyHistoryAbort.abort(); lobbyHistoryAbort = null; }
  });
}

/**
 * F14 from post-implementation Codex review — only subscribe to /chat WS
 * when the resulting messages have somewhere to render. CAPTAINS_ONLY-
 * non-captain mode hides the entire #lobby-chat block (session 75 contract);
 * subscribing in that mode would silently consume frames + ack them, leaving
 * the chat-button badge wrong post-start.
 */
function shouldSubscribeChatWs(): boolean {
  const s = getState();
  if (!s.teamName || !s.playerToken || !s.playerId) return false;
  const captainsOnlyLocked = s.lobbyChatMode === 'CAPTAINS_ONLY' && !s.isCaptain;
  return !captainsOnlyLocked;
}

/**
 * Subscribe to /ws/games/{code}/chat for org→team live messages, then
 * hydrate history. Helper for both the initial mount and the team-switch
 * path. Captures the current state.teamName as a stale-guard token so a
 * delayed history GET doesn't bleed results from the previous team into
 * the new lobby (F12 from /autoplan eng review).
 *
 * F15 from post-implementation Codex review — closure-captures the WS
 * instance and discards frames whose source instance is no longer the
 * current `chatWs`. This stops a queued frame from a closed-but-not-yet-
 * GC'd socket from appending old-team org messages into the new team's
 * lobby DOM after a team switch.
 */
function subscribeToTeamChat(): void {
  const state = getState();
  // Defensive — caller already checks, but guard the WS construction
  // against null teamName/token in case the call site changes.
  if (!state.teamName || !state.playerToken) return;

  const myWs = new ReconnectingWebSocket(
    wsUrl(`/ws/games/${state.gameCode}/chat?playerId=${state.playerId}`),
    () => {
      // Status changes are already surfaced by the lobby WS; the chat WS
      // shares the same network so we don't need a second reconnecting
      // banner. Silent.
    }
  );
  // F15: discard frames if `chatWs` has rotated since this listener was
  // registered. A close()-then-frame race leaves no other defense.
  myWs.on('CHAT_MESSAGE', (envelope: unknown) => {
    if (chatWs !== myWs) return;
    handleOrgChatMessage(envelope);
  });
  chatWs = myWs;
  myWs.connect();

  // Hydrate history. The fetch can resolve after a team-switch — guard
  // by capturing teamName + AbortController. The signal is forwarded to
  // `getChatMessages` (which passes it to `request()` → `fetch()`), so
  // the in-flight GET is cancelled at the network level on abort. The
  // post-await `signal.aborted` check provides belt-and-suspenders
  // coverage for the race where the response arrives concurrently with
  // the abort signal firing.
  historyAbort?.abort();
  const ctrl = new AbortController();
  historyAbort = ctrl;
  const forTeam = state.teamName;
  loadOrgHistory(forTeam, ctrl).catch(() => {
    // F17 from post-implementation Codex review — guard the failure
    // hint against aborted/stale fetches. A previous team's failed
    // history GET that resolves AFTER team-switch must not append
    // "история неполная" into the new team's lobby DOM.
    if (ctrl.signal.aborted) return;
    if (getState().teamName !== forTeam) return;
    appendHistoryIncompleteHint();
  });
}

/**
 * /investigate 2026-05-02 — Bug 1 helper.
 *
 * Render one LOBBY-channel row. Same wire shape as the live `LOBBY_CHAT`
 * WS frame and the historical `ChatMessageData` row — both carry
 * sender + senderName, message, timestamp, teamName, mediaUrl. Org rows
 * have teamName=null (live wire) or teamName='' (history; the legacy
 * `__LOBBY__` sentinel is normalized in `toChatMessage`); both fall to
 * `null` here via the `|| null` coalesce, which routes them to the
 * amber accent (DESIGN.md sacred-color rule).
 *
 * Pulled out of the original `ws.on('LOBBY_CHAT', ...)` body so history
 * hydration shares the exact same render path — no chance of drift.
 */
interface LobbyEventLike {
  sender?: string | null;
  senderName?: string | null;
  message?: string | null;
  timestamp?: number | null;
  teamName?: string | null;
  mediaUrl?: string | null;
}

function appendLobbyEvent(event: LobbyEventLike): void {
  const messagesDiv = document.getElementById('lobby-messages');
  if (!messagesDiv) return;
  const msg = document.createElement('div');
  msg.style.cssText = 'margin-bottom: 8px;';
  const tn: string | null = (event.teamName as string | null | undefined) || null;
  const senderColor = tn ? teamColor(tn) : 'var(--amber)';
  const titleAttr = tn ? ` title="Команда: ${escapeHtml(tn)}"` : '';
  const dataAttr = tn ? ` data-team-name="${escapeHtml(tn)}"` : '';
  const cursor = tn ? 'pointer' : 'default';
  const senderLabel = event.sender || event.senderName || '???';
  const senderHtml = `<span class="lobby-sender" style="color: ${senderColor}; font-weight: 600; cursor: ${cursor};"${titleAttr}${dataAttr}>${escapeHtml(senderLabel)}</span>`;
  const text = event.message || '';
  const mediaUrl: string | null = event.mediaUrl || null;
  let mediaHtml = '';
  if (mediaUrl) {
    const isVideo = /\.(mp4|webm)$/i.test(mediaUrl);
    mediaHtml = isVideo
      ? `<video src="${escapeHtml(mediaUrl)}" controls style="max-width:min(220px,70%);border-radius:var(--radius-md);display:block;margin-top:4px;"></video>`
      : `<img src="${escapeHtml(mediaUrl)}" alt="Фото" loading="lazy" style="max-width:min(220px,70%);border-radius:var(--radius-md);display:block;margin-top:4px;" />`;
  }
  const textHtml = text ? `: ${escapeHtml(text)}` : '';
  msg.innerHTML = `${senderHtml}${textHtml}${mediaHtml}`;
  messagesDiv.appendChild(msg);
  messagesDiv.scrollTop = messagesDiv.scrollHeight;
}

/**
 * /investigate 2026-05-02 — Bug 1 helper.
 *
 * Cold-mount hydration of LOBBY-channel rows for the player's view of
 * lobby chat. Renders each row via the shared `appendLobbyEvent` helper
 * (same DOM as live WS frames). Updates `lobbyHistoryCutoff` to the
 * highest seen timestamp so the WS handler can dedup any frame that
 * was already in the fetched payload.
 *
 * Errors are silent: a network blip on cold-mount is fine — the WS
 * will deliver the next message normally; the user just doesn't see
 * older history yet (same as before this fix). The signal is forwarded
 * to `getLobbyChatHistory` (which wires it into `fetch()`), so the
 * in-flight GET is cancelled at the network level on view destroy. The
 * post-await `signal.aborted` check provides belt-and-suspenders
 * coverage for the race where the response arrives concurrently with
 * abort.
 */
async function loadLobbyChatHistoryOnMount(
  code: string,
  playerId: string,
  signal: AbortSignal,
): Promise<void> {
  let messages: unknown;
  try {
    messages = await getLobbyChatHistory(code, playerId, signal);
  } catch {
    return;
  }
  if (signal.aborted) return;
  // Belt-and-suspenders — if the player navigated to /riddle or /join
  // mid-fetch, don't paint into a stale lobby DOM. The abort signal
  // covers explicit destroy paths; this covers WS-driven navigation
  // that bypasses destroy (GAME_STARTED handler runs destroy itself,
  // but a queued fetch can resolve into a torn-down view if the timing
  // is unlucky).
  if (typeof window !== 'undefined' && window.location.hash !== '#/lobby') return;
  // Server contract returns an array; treat anything else as an empty
  // payload so a misrouted/malformed response doesn't throw a TypeError
  // up the unhandled-rejection chain.
  if (!Array.isArray(messages)) return;
  let maxTs = lobbyHistoryCutoff;
  for (const m of messages as ChatMessageData[]) {
    appendLobbyEvent(m);
    if (typeof m.timestamp === 'number' && m.timestamp > maxTs) maxTs = m.timestamp;
  }
  lobbyHistoryCutoff = maxTs;
}

async function loadOrgHistory(forTeamName: string, ctrl: AbortController): Promise<void> {
  const state = getState();
  const messages = await getChatMessages(state.gameCode, state.playerId, ctrl.signal);
  if (ctrl.signal.aborted) return;
  if (getState().teamName !== forTeamName) return;
  const orgMessages = messages.filter((m) => m.senderType === 'ORGANIZER');
  for (const msg of orgMessages) {
    if (seenChatIds.has(msg.id)) continue;
    seenChatIds.add(msg.id);
    appendOrgRow(msg);
  }
  // F16 from post-implementation Codex review — DON'T call
  // acknowledgeChatThrough(messages) from the history path. A late-
  // resolving history GET ({m1,m2}) after a live frame already moved
  // chat-poll's lastSeenId to m3 would rewind the baseline to m2, causing
  // the next 15s poll to re-count m3 as unread. Live frames are the
  // authoritative ack signal (see handleOrgChatMessage). chat-poll's
  // first-poll baseline (chat-poll.ts:55-59) already handles the
  // "player joins mid-lobby with N historical org messages" case.
}

/**
 * WS frame handler. Backend ChatEvent envelope = { type: 'CHAT_MESSAGE',
 * message: ChatMessage }. Filter to org-targeted frames; dedup against
 * seenChatIds (which is also populated by history hydration — covers
 * the history-then-live AND live-then-history races, F3).
 */
function handleOrgChatMessage(envelope: unknown): void {
  const evt = envelope as { type: string; message?: ChatMessage };
  const msg = evt.message;
  if (!msg) return;
  if (msg.senderType !== 'ORGANIZER') return;
  if (seenChatIds.has(msg.id)) return;
  seenChatIds.add(msg.id);
  appendOrgRow(msg);
  // F10 + F16 — live frames are the authoritative ack signal. The user
  // just saw this message rendered in their lobby DOM, so chat-poll's
  // lastSeenId moves forward AND the unread badge resets (the user is
  // actively engaged with the lobby surface). Monotonic by construction:
  // live frames arrive in server-broadcast order.
  acknowledgeChatThrough([msg]);
  setState({ unreadChatCount: 0 });
}

/**
 * Render an org row into #lobby-messages. Mirrors chat-view.ts:163-179
 * org-bubble styling exactly (session-52 contract): bg-card surface,
 * 1px border, top-left radius collapsed, amber sender label.
 */
function appendOrgRow(msg: ChatMessage): void {
  const messagesDiv = document.getElementById('lobby-messages');
  if (!messagesDiv) return;
  const time = new Date(msg.timestamp).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const row = document.createElement('div');
  row.className = 'lobby-org-row';
  row.style.cssText =
    'display: flex; flex-direction: column; align-items: flex-start; margin-bottom: var(--sp-sm); gap: 4px;';
  let mediaHtml = '';
  if (msg.mediaUrl) {
    const isVideo = /\.(mp4|webm)$/i.test(msg.mediaUrl);
    mediaHtml = isVideo
      ? `<video src="${escapeHtml(msg.mediaUrl)}" controls playsinline preload="metadata" style="max-width:min(240px,70%);border-radius:var(--radius-md);display:block;margin-bottom:4px;"></video>`
      : `<img src="${escapeHtml(msg.mediaUrl)}" alt="Фото" loading="lazy" style="max-width:min(200px,60%);border-radius:var(--radius-md);display:block;margin-bottom:4px;" />`;
  }
  const textHtml = msg.message
    ? `<p class="text-body" style="word-break: break-word; color: var(--text-primary); margin: 0;">${escapeHtml(msg.message)}</p>`
    : '';
  row.innerHTML = `
    <div style="max-width: 80%; padding: var(--sp-sm) var(--sp-md);
                background: var(--bg-card);
                border: 1px solid var(--bg-border);
                border-radius: var(--radius-lg);
                border-top-left-radius: var(--radius-sm);
                color: var(--text-primary);">
      <p class="text-label" style="color: var(--amber); font-size: 12px; margin-bottom: 2px;">Организатор</p>
      ${mediaHtml}
      ${textHtml}
      <p style="font-size: 12px; color: var(--text-hint); text-align: right; margin-top: 2px;">${time}</p>
    </div>
  `;
  messagesDiv.appendChild(row);
  messagesDiv.scrollTop = messagesDiv.scrollHeight;
}

/**
 * Append a single low-priority hint row when history GET fails. Renders
 * once per loadOrgHistory failure; future failures from auto-reconnect
 * are silent (the user already knows).
 */
let historyHintShown = false;
function appendHistoryIncompleteHint(): void {
  if (historyHintShown) return;
  historyHintShown = true;
  const messagesDiv = document.getElementById('lobby-messages');
  if (!messagesDiv) return;
  const hint = document.createElement('div');
  hint.className = 'hint-row';
  hint.style.cssText =
    'color: var(--text-hint); font-size: 12px; padding: var(--sp-xs) 0; text-align: center;';
  hint.textContent = 'История неполная';
  messagesDiv.insertBefore(hint, messagesDiv.firstChild);
}

/**
 * Wire up the lobby kebab button. Creates a body-attached <ul role="menu">
 * with two menuitems:
 *   - «Код игры ABC123» → tap copies to clipboard + toast «Скопировано»
 *   - «Выйти из лобби» → confirm dialog → leaveGame + navigate('join')
 *
 * Returns a teardown function that removes the menu node AND unregisters
 * both the document click-outside listener and the Escape keydown listener.
 * /autoplan eng review CRITICAL-2: destroyLobbyView() must call this.
 *
 * Menu items use role="menuitem" + <button> (no role=button mutation via
 * wireClickToCopy — codex /autoplan design review flagged that as a
 * menu-semantics conflict). Click on the code row does the copy itself.
 */
function wireLobbyKebab(btn: HTMLButtonElement): () => void {
  const state = getState();
  const menu = document.createElement('div');
  menu.setAttribute('role', 'menu');
  menu.setAttribute('aria-label', 'Меню лобби');
  menu.style.cssText = 'display: none; position: fixed; background: var(--bg-card); border: 1px solid var(--amber); border-radius: 8px; padding: 4px 0; min-width: 240px; z-index: 100; box-shadow: 0 8px 24px rgba(0,0,0,0.5);';
  document.body.appendChild(menu);

  function makeItem(opts: { label: string; danger?: boolean; ariaLabel?: string; onClick: () => void | Promise<void>; }): HTMLButtonElement {
    const item = document.createElement('button');
    item.setAttribute('role', 'menuitem');
    item.setAttribute('type', 'button');
    if (opts.ariaLabel) item.setAttribute('aria-label', opts.ariaLabel);
    const color = opts.danger ? 'var(--danger)' : 'var(--text-primary)';
    item.style.cssText = `display: block; width: 100%; padding: 12px 16px; background: none; border: none; color: ${color}; font-size: 14px; text-align: left; cursor: pointer; white-space: nowrap; font-family: inherit;`;
    item.textContent = opts.label;
    item.addEventListener('click', () => {
      closeMenu();
      void opts.onClick();
    });
    return item;
  }

  // Item 1: Код игры — tap to copy.
  const codeItem = makeItem({
    label: `Код игры: ${state.gameCode}`,
    ariaLabel: `Копировать код игры ${state.gameCode}`,
    onClick: async () => {
      const ok = await copyToClipboard(state.gameCode);
      showCopyToast(ok ? 'Скопировано' : 'Не удалось скопировать');
    },
  });
  menu.appendChild(codeItem);

  // Item 2: Выйти из лобби — destructive.
  const leaveItem = makeItem({
    label: 'Выйти из лобби',
    danger: true,
    onClick: async () => {
      if (!confirm('Выйти из лобби? Вы вернётесь к выбору команды.')) return;
      const s = getState();
      try { await leaveGame(s.gameCode, s.playerId); } catch { /* best-effort */ }
      destroyLobbyView();
      navigate('join');
    },
  });
  menu.appendChild(leaveItem);

  let open = false;
  const positionMenu = () => {
    const rect = btn.getBoundingClientRect();
    menu.style.top = (rect.bottom + 4) + 'px';
    menu.style.right = (window.innerWidth - rect.right) + 'px';
    menu.style.left = 'auto';
  };
  const openMenu = () => {
    open = true;
    positionMenu();
    menu.style.display = 'block';
    btn.setAttribute('aria-expanded', 'true');
  };
  const closeMenu = () => {
    open = false;
    menu.style.display = 'none';
    btn.setAttribute('aria-expanded', 'false');
  };
  const toggle = (e?: Event) => {
    e?.stopPropagation();
    if (open) closeMenu(); else openMenu();
  };
  const onOutsideClick = (e: Event) => {
    if (!open) return;
    const target = e.target as Node;
    if (btn.contains(target) || menu.contains(target)) return;
    closeMenu();
  };
  const onEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && open) {
      closeMenu();
      btn.focus();
    }
  };

  btn.addEventListener('click', toggle);
  document.addEventListener('click', onOutsideClick);
  document.addEventListener('keydown', onEscape);

  return () => {
    btn.removeEventListener('click', toggle);
    document.removeEventListener('click', onOutsideClick);
    document.removeEventListener('keydown', onEscape);
    menu.remove();
  };
}

export function destroyLobbyView(): void {
  // Plan B Task 6 (2026-05-11) — lobbyReconcilePoll + visibility/pageshow
  // listeners + kebab teardown + store unsubscribe + both AbortControllers
  // are released via `activeLc.cleanup()`. The genuinely module-scoped bits
  // (ws, chatWs, reconcileGate, lobbyHistoryCutoff, lastTeamName,
  // seenChatIds, historyHintShown) stay in the explicit destroy below
  // because they're referenced by callbacks/closures outside the lc-managed
  // scope (WS message handlers; the gate is shared across renders for
  // inflight-recovery; ws/chatWs close needs to fire before we drop the
  // reference).
  activeLc?.cleanup();
  activeLc = null;

  if (ws) { ws.close(); ws = null; }
  // Session 87 — tear down chat WS subscription. Idempotent —
  // destroyLobbyView is called from the kebab leave-game flow, the
  // GAME_STARTED handler (defensive F2), and the router on view swap.
  // Multiple calls must not throw.
  if (chatWs) { chatWs.close(); chatWs = null; }
  seenChatIds.clear();
  lastTeamName = null;
  historyHintShown = false;
  // /investigate 2026-05-02 — Bug 1: reset the dedup cutoff. Without the
  // reset, a stale cutoff could swallow a legitimate WS frame on the NEXT
  // lobby mount (cross-game / re-join).
  lobbyHistoryCutoff = 0;
  // Mirror destroyLocationView / destroyChatView / destroySpectatorView: if
  // a reconcileLobbyAgainstServer call is in-flight when the view is torn
  // down, the gate's finally-block will eventually release the lock —
  // but any visibility/pageshow handler on the NEW mount fires first and
  // bails at the lock guard. Release here so the next mount's reconcile isn't
  // skipped.
  reconcileGate.release();
  // /investigate 2026-05-04 v0.80.7 — drop the connection-bar singleton.
  // It's a body-prepended global owned by lobby/chat/spectator only; the
  // next view (location/results/join) never imports connection-status and
  // can't sweep it. If a non-1008 onclose flipped the bar to «Переподключение…»
  // and the WS hadn't reconnected before destroy, the bar would otherwise
  // stay forever — observed live on prod game 025774, Android Chrome 147,
  // bar still showing on /results after game finished. Mirror chat-view
  // destroyChatView:1077 which already does this.
  hideStatus();
  // Plan B Task 10 (2026-05-11) — team-event-toast sweep is wired into the
  // lifecycle via `activeLc.onDestroy(dismissAllTeamToasts)` in
  // `renderLobbyView` (registered unconditionally because `showCopyToast`
  // is LC-free by design and never registers with bodyOverlays itself).
  // `activeLc?.cleanup()` above runs the sweep — same effect as the
  // pre-extraction explicit `dismissAllTeamToasts()` call, but routed
  // through the lifecycle primitive.
}
